import { useT } from "@/i18n/I18nContext";
import { CheckCircle2, Clock, AlertTriangle, Circle } from "lucide-react";

/**
 * Visual badge for rental payment status.
 *
 * Props:
 *  - paymentStatus: object from backend (status, balance_due, next_due_date, days_until_due, ...)
 *  - size: "sm" | "md" (default "md")
 *  - showAmount: bool (default false) — show balance due value
 */
export default function PaymentStatusBadge({ paymentStatus, size = "md", showAmount = false }) {
  const { t } = useT();
  if (!paymentStatus || paymentStatus.status === "n_a" || paymentStatus.status === "not_started") return null;

  const s = paymentStatus.status;
  const cfg = {
    paid:      { Icon: CheckCircle2,  cls: "bg-emerald-50 text-emerald-700 border-emerald-300",  dot: "bg-emerald-500", label: t("paymentStatus.paid") },
    due_soon:  { Icon: Clock,         cls: "bg-amber-50 text-amber-700 border-amber-300",         dot: "bg-amber-500",   label: t("paymentStatus.dueSoon") },
    overdue:   { Icon: AlertTriangle, cls: "bg-rose-50 text-rose-700 border-rose-300",            dot: "bg-rose-500",    label: t("paymentStatus.overdue") },
    unknown:   { Icon: Circle,        cls: "bg-zinc-50 text-zinc-500 border-zinc-200",            dot: "bg-zinc-400",    label: t("paymentStatus.unknown") },
  }[s] || { Icon: Circle, cls: "bg-zinc-50 text-zinc-500 border-zinc-200", dot: "bg-zinc-400", label: s };

  const Icon = cfg.Icon;
  const tight = size === "sm";
  const dueIn = paymentStatus.days_until_due;
  let detail = "";
  if (s === "paid" && dueIn != null) {
    if (dueIn > 0) detail = t("paymentStatus.nextIn", { days: dueIn });
    else detail = t("paymentStatus.upToDate");
  } else if (s === "due_soon" && dueIn != null) {
    if (dueIn === 0) detail = t("paymentStatus.today");
    else if (dueIn === 1) detail = t("paymentStatus.tomorrow");
    else if (dueIn > 0) detail = t("paymentStatus.inDays", { days: dueIn });
    else detail = t("paymentStatus.daysLate", { days: Math.abs(dueIn) });
  } else if (s === "overdue" && dueIn != null) {
    detail = t("paymentStatus.daysLate", { days: Math.abs(dueIn) });
  }

  const balance = paymentStatus.balance_due;

  return (
    <div
      className={`inline-flex items-center gap-1.5 border ${cfg.cls} ${tight ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-[11px]"} font-mono uppercase tracking-wider`}
      data-testid={`payment-status-${s}`}
      title={paymentStatus.next_due_date ? `${t("paymentStatus.nextDue")}: ${paymentStatus.next_due_date}` : undefined}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${s !== "paid" ? "animate-pulse" : ""}`} />
      <Icon className={tight ? "w-2.5 h-2.5" : "w-3 h-3"} />
      <span className="font-semibold">{cfg.label}</span>
      {detail && <span className="opacity-70">· {detail}</span>}
      {showAmount && balance > 0 && (
        <span className="font-bold ml-1">${balance.toFixed(2)}</span>
      )}
    </div>
  );
}
